import { first } from 'rxjs/operators'
import { Component, Watch } from 'vue-property-decorator'

import { Item, Movement, getTranslatedString, LinkableItemLifecycleStates, Location, SearchQuery } from '@skinsoft/types'
import { itemSelectors, locationSelectors, movementSelectors } from '@skinsoft/selectors'
import { MovementDataStore } from '@skinsoft/entity'
import { LocationDataStore } from '@skinsoft/entity'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { StateInput, ValidationStates } from '@skinsoft/ui'
import { LocationSelectorPopup } from '@skinsoft/entity-ui/src/location/location-selector-popup'

@Component({
    components: {
        sLocationSelectorPopup: LocationSelectorPopup,
        sStateInput: StateInput,
    },
    props: ['item', 'movement', 'placeholder'],
    template: require('./location-input.html'),
})
export class LocationInput extends ReactiveVue {

    get realTimeLocation() {
        return itemSelectors.realTimeLocation(this.item);
    }

    get linkableItemState() {
        return this.movement ? movementSelectors.linkableItemState(this.movement, this.item.id) : null;
    }

    public item: Item;
    public movement: Movement;
    private placeholder: string

    public $locationDataStore: LocationDataStore;
    public $movementDataStore: MovementDataStore;
    public ValidationStates = ValidationStates;

    public readonly $refs: {
        stateInput: StateInput;
        locationPopup: LocationSelectorPopup;
    };

    public itemSelectors = itemSelectors;

    private selectedLocation: Location = null as any;
    private permanentLocationHint: string = "";

    private locations: Location[] | null = null;

    public mounted() {
        this.subscribeToSyncQueue(this.syncLinkableItemState.bind(this));
        this.triggerSync();
    }

    public async validator(value: string) {
        if (value) {
            try {
                const searchQuery: SearchQuery = {
                    queryText: value,
                }

                this.selectedLocation = (await this.$locationDataStore.searchAll(searchQuery).pipe(first()).toPromise()).allData[0];
                if (!this.selectedLocation) {
                    this.selectedLocation = {id: value, translatedTitle: {translatedString: {fr: ""}}}
                }
                return true;
            } catch (error) {
                return false;
            }
        }
    }

    private syncLinkableItemState() {
        if (!this.$refs.stateInput) {
            // for some reason it is undefined for a while after mount
            return;
        }

        if (!this.movement) {
            switch (this.linkableItemState) {
                case LinkableItemLifecycleStates.FINALIZED:
                    this.$refs.stateInput.validate(this.realTimeLocation);
                    break;
                case LinkableItemLifecycleStates.SELECTED:
                case LinkableItemLifecycleStates.IN_TRANSIT:
                    this.$refs.stateInput.reset();
                    break;
            }
        }
    }

    private openPopup() {
        this.$refs.locationPopup.open()
    }

    private onLocationValidated(locationValidatedEvent) {
        let locationTitle = locationSelectors.title(locationValidatedEvent.location, this.$i18n.locale) 
        // the translatedTitle is included in the title selector, but I'm still wondering if we should let 
        //translatedTitle inside title selector, so I let the line bellow
        if(!locationTitle) {
            locationSelectors.translatedTitle(locationValidatedEvent.location, this.$i18n.locale)
        }
        
        this.$refs.stateInput.setValue(locationTitle)
        this.selectedLocation = locationValidatedEvent.location;
        this.$emit('change', this.selectedLocation)
    }

    private subscribeToLocationHint() {
        // Retrieving permanent location as a hint to display
        const permanentLocationId = itemSelectors.permanentLocation(this.item);
        if (permanentLocationId) {
            this.subscribeTo(
                this.$locationDataStore.fetchOne(
                    permanentLocationId
                ).pipe(first()),
                async (location) => {
                    this.permanentLocationHint = await this.$locationDataStore.resolvePath(locationSelectors.pathIds(location), this.$i18n.locale)
                },
                'permanent-location-hint-subscription')
        }
    }

    private unsubscribeFromLocationHint() {
        this.unsubscribeFrom('permanent-location-hint-subscription')
    }

    @Watch('linkableItemState', { immediate: false, deep: true })
    private onLinkableItemStateChange() {
        this.triggerSync();
    }

    @Watch('item', { immediate: true })
    private onitemChange() {
        this.unsubscribeFromLocationHint();
        this.subscribeToLocationHint();
    }
}
